import { useTheme } from '@mui/material';
import { editor } from 'monaco-editor';
import { useEffect, useRef } from 'react';

// Theme IDs
export const DARK_THEME_ID = 'sda-dark-theme';
export const LIGHT_THEME_ID = 'sda-light-theme';

/**
 * Hook to generate Monaco editor themes based on the current MUI theme
 * @returns Object containing theme IDs and theme data for Monaco editor
 */
export const useMonacoTheme = (transparentBackground = false) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const currentThemeId = isDarkMode ? DARK_THEME_ID : LIGHT_THEME_ID;
  const monacoEditorRef = useRef<typeof editor | null>(null);

  const backgroundColor = transparentBackground
    ? `${theme.palette.background.paper}00`
    : theme.palette.background.paper;

  /**
   * Creates a dark theme configuration for Monaco editor using the current MUI theme
   */
  const getDarkTheme = (): editor.IStandaloneThemeData => ({
    base: 'vs-dark',
    inherit: true,
    rules: [
      {
        token: 'comment',
        foreground: theme.palette.text.additional,
        fontStyle: 'italic',
      },
      {
        token: 'keyword',
        foreground: theme.palette.status.blue.text.replace('#', ''),
        fontStyle: 'bold',
      },
      {
        token: 'string',
        foreground: theme.palette.status.green.text.replace('#', ''),
      },
      {
        token: 'number',
        foreground: theme.palette.common.orange.replace('#', ''),
      },
      {
        token: 'operator',
        foreground: theme.palette.text.secondary.replace('#', ''),
      },
      {
        token: 'identifier',
        foreground: theme.palette.text.primary.replace('#', ''),
      },
      {
        token: 'type',
        foreground: theme.palette.status.purple.text.replace('#', ''),
      },
      {
        token: 'variable',
        foreground: theme.palette.text.primary.replace('#', ''),
      },
      {
        token: 'function',
        foreground: theme.palette.primary.main.replace('#', ''),
      },
      {
        token: 'constant',
        foreground: theme.palette.status.orange.text.replace('#', ''),
      },
      { token: 'tag', foreground: theme.palette.danger.main.replace('#', '') },
      {
        token: 'timestamp',
        foreground: theme.palette.text.secondary.replace('#', ''),
        fontStyle: 'italic',
      },
    ],
    colors: {
      'editor.foreground': theme.palette.text.primary,
      'editor.background': backgroundColor,
      'editorCursor.foreground': theme.palette.text.primary,
      'editor.lineHighlightBackground': theme.palette.divider,
      'editorLineNumber.foreground': theme.palette.text.additional,
      'editorLineNumber.activeForeground': theme.palette.text.secondary,
      'editor.selectionBackground': theme.palette.stroke.secondary,
      'editor.inactiveSelectionBackground': theme.palette.status.blue.stroke,
      'editorBracketMatch.background': theme.palette.stroke.secondaryLight,
      'editorBracketMatch.border': theme.palette.primary.main,
      'editorIndentGuide.background': theme.palette.divider,
      'editorIndentGuide.activeBackground': theme.palette.stroke.secondaryLight,
      'editorWhitespace.foreground': theme.palette.tag.stroke,
      'editorOverviewRuler.border': theme.palette.divider,
      'editorGutter.background': backgroundColor,
      'editorError.foreground': theme.palette.danger.main,
      'editorWarning.foreground': theme.palette.status.orange.text,
      'editorInfo.foreground': theme.palette.status.blue.text,
      'scrollbarSlider.background': `${theme.palette.divider}80`,
      'scrollbarSlider.hoverBackground': `${theme.palette.tag.stroke}A0`,
      'scrollbarSlider.activeBackground': `${theme.palette.stroke.secondaryLight}A0`,
      'minimap.background': backgroundColor,
      'minimap.selectionHighlight': theme.palette.stroke.secondary,
      focusBorder: backgroundColor,
    },
  });

  /**
   * Creates a light theme configuration for Monaco editor using the current MUI theme
   */
  const getLightTheme = (): editor.IStandaloneThemeData => ({
    base: 'vs',
    inherit: true,
    rules: [
      {
        token: 'comment',
        foreground: theme.palette.text.additional.replace('#', ''),
        fontStyle: 'italic',
      },
      {
        token: 'keyword',
        foreground: theme.palette.primary.main.replace('#', ''),
        fontStyle: 'bold',
      },
      {
        token: 'string',
        foreground: theme.palette.status.green.text.replace('#', ''),
      },
      {
        token: 'number',
        foreground: theme.palette.status.orange.text.replace('#', ''),
      },
      {
        token: 'operator',
        foreground: theme.palette.text.secondary.replace('#', ''),
      },
      {
        token: 'identifier',
        foreground: theme.palette.text.primary.replace('#', ''),
      },
      {
        token: 'type',
        foreground: theme.palette.status.purple.text.replace('#', ''),
      },
      {
        token: 'variable',
        foreground: theme.palette.text.primary.replace('#', ''),
      },
      {
        token: 'function',
        foreground: theme.palette.primary.main.replace('#', ''),
      },
      {
        token: 'constant',
        foreground: theme.palette.status.orange.text.replace('#', ''),
      },
      { token: 'tag', foreground: theme.palette.danger.main.replace('#', '') },
      {
        token: 'timestamp',
        foreground: theme.palette.text.secondary.replace('#', ''),
        fontStyle: 'italic',
      },
    ],
    colors: {
      'editor.foreground': theme.palette.text.primary,
      'editor.background': backgroundColor,
      'editorCursor.foreground': theme.palette.text.primary,
      'editor.lineHighlightBackground': theme.palette.background.default,
      'editorLineNumber.foreground': theme.palette.text.secondary,
      'editorLineNumber.activeForeground': theme.palette.text.additional,
      'editor.selectionBackground': theme.palette.primary.light,
      'editor.inactiveSelectionBackground': theme.palette.stroke.secondaryLight,
      'editorBracketMatch.background': theme.palette.stroke.secondary,
      'editorBracketMatch.border': theme.palette.primary.main,
      'editorIndentGuide.background': theme.palette.divider,
      'editorIndentGuide.activeBackground': theme.palette.stroke.secondary,
      'editorWhitespace.foreground': theme.palette.secondary.light,
      'editorOverviewRuler.border': theme.palette.divider,
      'editorGutter.background': backgroundColor,
      'editorError.foreground': theme.palette.danger.main,
      'editorWarning.foreground': theme.palette.status.orange.text,
      'editorInfo.foreground': theme.palette.status.blue.text,
      'scrollbarSlider.background': `${theme.palette.divider}80`,
      'scrollbarSlider.hoverBackground': `${theme.palette.tag.stroke}A0`,
      'scrollbarSlider.activeBackground': theme.palette.stroke.secondary,
      'minimap.background': backgroundColor,
      'minimap.selectionHighlight': theme.palette.primary.light,
      focusBorder: backgroundColor,
    },
  });

  /**
   * Register Monaco editor themes
   * @param monaco The Monaco editor instance
   */
  const registerThemes = (monaco: typeof editor) => {
    monacoEditorRef.current = monaco;

    // Define both themes using current theme values
    monaco.defineTheme(DARK_THEME_ID, getDarkTheme());
    monaco.defineTheme(LIGHT_THEME_ID, getLightTheme());

    // Set the current theme
    monaco.setTheme(currentThemeId);
  };

  // Update the theme when the app theme changes
  useEffect(() => {
    if (monacoEditorRef.current) {
      // Redefine themes with updated colors from the theme
      monacoEditorRef.current.defineTheme(DARK_THEME_ID, getDarkTheme());
      monacoEditorRef.current.defineTheme(LIGHT_THEME_ID, getLightTheme());

      // Apply the current theme
      monacoEditorRef.current.setTheme(currentThemeId);
    }
  }, [theme.palette.mode]); // Only re-run when the theme mode changes

  return {
    currentThemeId,
    registerThemes,
    getDarkTheme,
    getLightTheme,
  };
};
